import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, ArrowRight, AlertCircle, CheckCircle2, ShieldQuestion } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/api';
import { TurnstileBox } from '@/components/TurnstileBox';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [showTurnstile, setShowTurnstile] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined);

  const navigate = useNavigate();
  
  const resetPath = '/reset-password';
  const registerPath = '/register';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiFetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, turnstile_token: turnstileToken })
      });

      const data = await response.json();

      if (response.status === 403 && data.requiresCaptcha) {
        setShowTurnstile(true);
        setLoading(false);
        toast.info('Security verification required');
        return;
      }

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          navigate(`${resetPath}?email=${encodeURIComponent(email)}`);
        }, 2000);
      } else {
        setError(data.error || 'Email not found');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-20 relative overflow-hidden">
      <SEO title="Forgot Password" noindex />
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_50%_0%,rgba(0,0,0,0.02)_0%,transparent_50%)]" />
      </div>

      <div className="w-full max-w-[400px] relative z-10 transition-all duration-700">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-5xl font-bold font-display text-black uppercase tracking-tighter mb-3 leading-none">
            Reset <br />
            <span className="text-neon-cyan">Password</span>
          </h1>
          <p className="text-zinc-600 font-medium italic text-sm">Enter your email to receive a code.</p>
        </div>

        <div className="bg-white p-10 rounded-[3rem] border border-black/5 shadow-2xl shadow-black/5">
          {!success ? (
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="space-y-3">
                <label className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-600 ml-1">Email Connection</label>
                <div className="relative group/input">
                  <Mail className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500 group-focus-within/input:text-black transition-colors" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-14 pr-6 py-5 bg-zinc-50/50 border border-black/5 rounded-2xl text-black font-medium placeholder:text-zinc-400 focus:ring-4 focus:ring-black/5 focus:border-black outline-none transition-all"
                    placeholder="name@example.com"
                  />
                </div>
              </div>

              <TurnstileBox 
                show={showTurnstile} 
                onVerify={(token) => setTurnstileToken(token)} 
              />

              {error && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-2xl text-red-600 text-[10px] font-black uppercase tracking-widest text-center animate-in fade-in slide-in-from-top-2">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-16 bg-black text-white hover:bg-zinc-800 font-black uppercase tracking-[0.2em] text-xs rounded-2xl shadow-2xl shadow-black/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? 'Sending...' : (
                  <>
                    Send Code <ArrowRight className="w-5 h-5 ml-1" />
                  </>
                )}
              </Button>
            </form>
          ) : (
            <div className="text-center py-10 space-y-8">
              <div className="w-24 h-24 bg-black rounded-[2.5rem] flex items-center justify-center mx-auto shadow-2xl shadow-black/20">
                <CheckCircle2 className="text-white w-12 h-12" />
              </div>
              <div>
                <h2 className="text-3xl font-bold font-display text-black uppercase tracking-tight mb-3">Code Sent</h2>
                <p className="text-zinc-500 font-medium leading-relaxed italic">Check your email for the verification code.</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="mt-12 text-center">
           <Link to="/login" className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-600 hover:text-black transition-colors">
              Return to Login
           </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
